import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, Subject, catchError, debounceTime, filter, map, of, switchMap, takeUntil } from 'rxjs';
import {
  KapchargeSearchResult,
  KapchargeSearchResultResponse,
  PaymentProcessingService,
} from '@lmc-app/core/services/payment-processing.service';
import { FormatUtil } from '@lmc-app/shared/utils/format.util';
import { KapchargeTransactionStatus } from '@lmc-app/shared/enum/kapcharge-transaction-status.enum';
import { fundingTypeLabel } from '@lmc-app/loan-servicing/payment-processing/funding-type.constant';

/**
 * How long the operator has to stop typing before the term is sent. Long enough that a
 * whole name is one request rather than one per keystroke, short enough that results still
 * feel like a response to typing.
 */
const SEARCH_DEBOUNCE_MS = 500;

/**
 * Shortest term that will be sent. A single character matches `%a%` against every
 * transaction joined to its customer, which is an expensive way to answer a question
 * nobody asked — one character is never the term someone meant to search for.
 */
const MIN_SEARCH_LENGTH = 2;

/** What a query needs; paging re-runs the same term without going back through the debounce. */
interface SearchQuery {
  term: string;
  page: number;
}

const EMPTY_RESPONSE: KapchargeSearchResultResponse = {
  results: [],
  rowCount: 0,
  currentPage: 1,
  pageSize: 0,
  pageCount: 0,
};

/**
 * A failed query, carried through the same stream as a successful one so the switchMap
 * keeps its cancellation semantics. `failed` is what tells an error apart from a genuine
 * "nothing matched" — without it the two look identical, and a request that never came
 * back would be reported as an empty result set.
 */
type QueryOutcome = { response: KapchargeSearchResultResponse; failed: boolean };

/**
 * Global transaction search.
 *
 * The list page behind this dialog knows only about batches, so an operator holding a loan
 * number or a customer name — and no idea which batch holds the row — had no way in. This
 * searches every batch at once and closes with the batch id of the row picked, which the
 * list then opens.
 *
 * Read-only by design: it answers "where is this payment and what happened to it". Every
 * correction is still made on the batch page the result leads to.
 */
@Component({
  selector: 'app-transaction-search-dialog',
  templateUrl: './transaction-search-dialog.component.html',
  styleUrls: ['./transaction-search-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatTooltipModule,
  ],
})
export class TransactionSearchDialogComponent implements OnInit, OnDestroy {
  readonly FormatUtil = FormatUtil;

  /** Shared palette, so a status reads the same here as on the cards and the batch page. */
  readonly getStatusColor = KapchargeTransactionStatus.getColor;

  readonly displayedColumns = ['loanNumber', 'customerName', 'status', 'dueDate', 'amount', 'batch'];

  readonly searchControl = new FormControl('', { nonNullable: true });

  /** The term the rows on screen belong to — the debounced value, not what is being typed. */
  term = '';

  /**
   * The term last sent to the server, or null when nothing on screen answers a query —
   * which is the case from the moment the text is edited until the next result lands.
   *
   * This, not `distinctUntilChanged`, is what decides whether a settled term is worth
   * sending. The operator can type `Jane` → `Jan` → `Jane` inside one debounce window:
   * `distinctUntilChanged` remembers only its own last emission, so it would swallow that
   * final `Jane` as a duplicate and leave the cleared table and its spinner up forever.
   * Comparing against what was actually queried cannot drift from what is displayed.
   */
  private lastQueriedTerm: string | null = null;

  results: KapchargeSearchResult[] = [];
  totalRows = 0;
  pageSize = 25;
  currentPage = 0;
  isLoading = false;

  readonly minSearchLength = MIN_SEARCH_LENGTH;

  /** Set when the last query failed; shown in the results area, never under the input. */
  errorMessage: string | null = null;

  /**
   * Every query, from either source: a settled new term, or a page change on the term
   * already showing. Both feed one switchMap, so a request is abandoned the moment it is
   * superseded and results can never arrive out of order.
   */
  private readonly query$ = new Subject<SearchQuery>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly paymentProcessingService: PaymentProcessingService,
    private readonly dialogRef: MatDialogRef<TransactionSearchDialogComponent, string | undefined>,
  ) {}

  ngOnInit(): void {
    // Every keystroke, undebounced: the moment the text differs from the term the rows
    // answer, those rows are the answer to a question no longer being asked. They go, and
    // the spinner takes over — rather than sitting there looking current for half a second
    // and then being replaced by something different.
    this.searchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        const term = value.trim();
        // Trimmed-identical text (a trailing space, say) queries nothing, so the rows on
        // screen still answer it — clearing them here would blank the table for good.
        if (term === this.lastQueriedTerm) return;

        this.term = term;
        this.results = [];
        this.totalRows = 0;
        this.errorMessage = null;
        this.currentPage = 0;
        this.lastQueriedTerm = null;
        // Below the minimum nothing will be sent, so there is nothing to wait for.
        this.isLoading = term.length >= MIN_SEARCH_LENGTH;
      });

    // Typing, debounced: send the term once the operator stops, and only if it differs
    // from the one already queried (the trim happens first, so a stray space sends
    // nothing).
    this.searchControl.valueChanges
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        map((value) => value.trim()),
        filter((term) => term !== this.lastQueriedTerm),
        takeUntil(this.destroy$),
      )
      .subscribe((term) => {
        this.term = term;
        // A new term is a new result set; page 2 of the previous one means nothing here.
        this.currentPage = 0;
        this.query$.next({ term, page: 0 });
      });

    this.query$
      .pipe(
        switchMap((query) => this.runQuery(query)),
        takeUntil(this.destroy$),
      )
      .subscribe(({ response, failed }) => {
        this.results = response.results ?? [];
        this.totalRows = response.rowCount ?? 0;
        this.errorMessage = failed
          ? 'The search could not be completed. Check your connection and try again.'
          : null;
        this.isLoading = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Issues one search, or resolves to an empty result set for a term too short to send.
   *
   * catchError is INSIDE the switchMap, so a failure ends this query rather than the
   * stream — on the outer pipe it would unsubscribe the whole thing and leave the box
   * dead for the rest of the dialog's life. The failure is reported as data rather than
   * swallowed: a request that timed out is not the same answer as "nothing matched".
   */
  private runQuery({ term, page }: SearchQuery): Observable<QueryOutcome> {
    if (term.length < MIN_SEARCH_LENGTH) {
      this.isLoading = false;
      return of({ response: EMPTY_RESPONSE, failed: false });
    }

    this.isLoading = true;
    this.errorMessage = null;
    this.lastQueriedTerm = term;
    return this.paymentProcessingService
      .searchTransactions({ search: term, page: page + 1, pageSize: this.pageSize })
      .pipe(
        map((response) => ({ response, failed: false })),
        catchError(() => of({ response: EMPTY_RESPONSE, failed: true })),
      );
  }

  /** Re-runs the query that failed, from the Try again button on the error placeholder. */
  onRetry(): void {
    this.query$.next({ term: this.term, page: this.currentPage });
  }

  /** True while a query is in flight. Rows are always cleared first, so this owns the view. */
  get showSpinner(): boolean {
    return this.isLoading;
  }

  /** Paging re-runs the current term straight away — the debounce is for typing only. */
  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    // The rows shown are the previous page, not this one. `totalRows` is kept so the
    // paginator stays put instead of vanishing and coming back under the operator's click.
    this.results = [];
    this.query$.next({ term: this.term, page: event.pageIndex });
  }

  onClear(): void {
    this.searchControl.setValue('');
    this.errorMessage = null;
    this.lastQueriedTerm = null;
  }

  /**
   * Hands the batch id back to the list, which opens it. The dialog does not navigate
   * itself: routing from inside would leave it to close over a page already changing under
   * it, and the list is the one that owns this route's child outlet.
   */
  onRowClick(row: KapchargeSearchResult): void {
    this.dialogRef.close(row.batchId);
  }

  trackByRow(_index: number, row: KapchargeSearchResult): number {
    return row.id;
  }

  /** True once a real search has run, succeeded, and come back with nothing. */
  get showEmptyState(): boolean {
    return !this.isLoading
      && !this.errorMessage
      && this.term.length >= MIN_SEARCH_LENGTH
      && !this.results.length;
  }

  /** True before the first usable term — the dialog opens on this, not on an empty table. */
  get showPrompt(): boolean {
    return !this.isLoading && !this.errorMessage && this.term.length < MIN_SEARCH_LENGTH;
  }

  /**
   * The loan account's name, when that — and not the displayed customer name — is why this
   * row matched; otherwise null.
   *
   * The customer name now carries the CIF's middle name, so most of what used to need
   * explaining explains itself. What is left is the loan account holding a word the CIF
   * does not have at all (234 of 7,655 loans on the mirror DB). Without this the row looks
   * like a search that matched nothing on screen.
   *
   * Word-by-word, matching the backend's rule: every word of the term is in the account
   * name, and at least one of them is not in the customer name.
   */
  matchedAccountName(row: KapchargeSearchResult): string | null {
    const accountName = row.accountName;
    if (!accountName || accountName === row.customerName) return null;

    const words = this.term.toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return null;

    const account = accountName.toLowerCase();
    const customer = (row.customerName ?? '').toLowerCase();
    if (!words.every((word) => account.includes(word))) return null;
    if (words.every((word) => customer.includes(word))) return null;

    return accountName;
  }

  /** "EFT · Debenture" for the Batch column; falls back to EFT the way the cards do. */
  batchLabel(row: KapchargeSearchResult): string {
    const fileType = row.fileType || 'EFT';
    return row.fundingType ? `${fileType} · ${fundingTypeLabel(row.fundingType)}` : fileType;
  }

  batchDate(row: KapchargeSearchResult): string {
    return row.batchCreatedAt ? FormatUtil.formatDate(row.batchCreatedAt, 'YYYY-MM-DD') : '—';
  }

  /**
   * Due dates come back as datetimes here (unlike the batch summary's date-only strings),
   * so they are formatted rather than printed raw.
   */
  dueDate(row: KapchargeSearchResult): string {
    return row.dueDate ? FormatUtil.formatDate(row.dueDate, 'YYYY-MM-DD') : '—';
  }
}
